import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react'
import { addDays, differenceInCalendarDays, format, startOfDay } from 'date-fns'
import React from 'react'

interface CalendarNavHeaderProps {
    startDate: string,
    setStartDate: (data: string) => void
    endDate: string,
    setEndDate: (date: string) => void
    isDesktop: boolean
}

/**
 * Header navigasi kalender Room Maintenance (←/TODAY/→), mengikuti pola
 * AreaHeaderContent di pms-calendar tapi judulnya "Rooms Maintenance".
 *
 * Beda dari AreaHeaderContent: geser berdasarkan PANJANG SPAN AKTIF
 * (differenceInCalendarDays(end,start)+1), bukan hardcode 30 hari — supaya
 * kalau user jump-to-date ke rentang 21-63 hari lalu klik ←/→, rentangnya
 * tetap dipertahankan (tidak collapse balik ke 30 hari).
 */
const CalendarNavHeader: React.FC<CalendarNavHeaderProps> = ({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isDesktop
}) => {
    const getSpanDays = () => {
        const span = differenceInCalendarDays(startOfDay(new Date(endDate)), startOfDay(new Date(startDate))) + 1;
        return span > 0 ? span : 30;
    };

    const handleMoveBack = () => {
        const spanDays = getSpanDays();
        const currentStartDate = startOfDay(new Date(startDate));
        const newStartDate = addDays(currentStartDate, -spanDays);
        const newEndDate = addDays(newStartDate, spanDays - 1);

        setStartDate(format(newStartDate, 'yyyy-MM-dd'));
        setEndDate(format(newEndDate, 'yyyy-MM-dd'));
    }

    const handleResetToday = () => {
        const today = startOfDay(new Date());
        const newEndDate = addDays(today, 30);

        setStartDate(format(today, 'yyyy-MM-dd'));
        setEndDate(format(newEndDate, 'yyyy-MM-dd'));
    }

    const handleMoveNext = () => {
        const spanDays = getSpanDays();
        const currentEndDate = startOfDay(new Date(endDate));
        const newEndDate = addDays(currentEndDate, spanDays);
        const newStartDate = addDays(newEndDate, -(spanDays - 1));

        setStartDate(format(newStartDate, 'yyyy-MM-dd'));
        setEndDate(format(newEndDate, 'yyyy-MM-dd'));
    }

    return (
        <div className="d-block w-100">
            <div className="d-flex justify-content-between w-100">
                <h3 className="fs-2 text-secondary d-none d-sm-block">Rooms Maintenance</h3>
            </div>

            {/* resourceAreaWidth kalender ini responsif lewat prop `isDesktop`
                (lihat CalendarComponent.tsx: "20%" di >=992px, FIXED 150px di
                bawah itu) — jadi layout tombol nav di sini WAJIB ikut cabang
                yang SAMA, bukan breakpoint CSS sendiri, supaya keduanya selalu
                sinkron. Desktop (kolom lega, "20%"): ←/TODAY/→ sejajar satu
                baris (mirip AreaHeaderContent di pms-calendar). Mobile/tablet
                (kolom sempit, 150px): TODAY full-width di baris sendiri, ←/→
                berbagi baris di bawahnya — 3 tombol sejajar di 150px akan
                overflow & memaksa kolom melebar (kolom jadi "tidak rapi"). */}
            {isDesktop ? (
                <div className="d-flex mt-2 w-100 m-auto">
                    <button
                        className="btn btn-sm btn-outline-primary btn-pill me-1"
                        onClick={() => handleMoveBack()}
                    >
                        <IconArrowLeft size={12} />
                    </button>

                    <button
                        className="btn btn-sm btn-outline-primary w-100 btn-pill me-1 d-flex align-items-center justify-content-center"
                        onClick={() => handleResetToday()}
                    >
                        TODAY
                    </button>

                    <button
                        className="btn btn-sm btn-outline-primary btn-pill"
                        onClick={() => handleMoveNext()}
                    >
                        <IconArrowRight size={12} />
                    </button>
                </div>
            ) : (
                <div className="d-flex flex-column w-100 mt-2">
                    <button
                        className="btn btn-sm btn-outline-primary btn-pill w-100 mb-1 d-flex align-items-center justify-content-center"
                        onClick={() => handleResetToday()}
                    >
                        TODAY
                    </button>

                    <div className="d-flex w-100">
                        <button
                            className="btn btn-sm btn-outline-primary btn-pill flex-fill me-1 d-flex align-items-center justify-content-center"
                            onClick={() => handleMoveBack()}
                        >
                            <IconArrowLeft size={12} />
                        </button>

                        <button
                            className="btn btn-sm btn-outline-primary btn-pill flex-fill d-flex align-items-center justify-content-center"
                            onClick={() => handleMoveNext()}
                        >
                            <IconArrowRight size={12} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CalendarNavHeader
